<template>
  <div class="relative">
    <SimpleTable class="full-height" card-wrap data-cy="tag-list-card">
      <thead>
        <tr>
          <th>
            <div class="d-flex align-items-center">
              <i class="fa-regular fa-tag mr-2"></i>
              <span class="info-header text-sm font-bold mr-2">Tags</span>
              <div class="number-badge">
                {{ selectedCustomer?.tags?.length ?? 0 }}
              </div>
              <div v-if="selectedCustomer?.tags" class="sera-ml-auto">
                <i
                  v-if="selectedCustomer.tags.length < maxTags"
                  class="ml-1 align-middle fa-regular fa-square-plus tag-menu-btn"
                  aria-label="Add Tag"
                  @click="toggleTagMenu"
                ></i>
                <TagMenu
                  v-if="showTagMenu"
                  :taggable-type="'customer'"
                  :tag-count="selectedCustomer?.tags?.length ?? 0"
                  @close-menu="toggleTagMenu"
                  @add-tag="handleAddTag"
                  @open-tag-modal="toggleNewTagModal"
                />
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <template
        v-if="selectedCustomer?.tags && selectedCustomer.tags.length === 0"
      >
        <tr class="list-item">
          <td>No Tags</td>
        </tr>
      </template>
      <template v-else>
        <tr class="list-item">
          <td colspan="2">
            <TagList
              :tags="selectedCustomer?.tags ?? []"
              :config="tagListConfig"
              @remove-tag="handleRemoveTag"
            />
          </td>
        </tr>
      </template>
    </SimpleTable>
    <TagModal
      v-if="showTagModal"
      :tag="newTag"
      @hide-modal="toggleNewTagModal"
      @save-new-success="handleSaveSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { defineProps, ref, watch } from "vue";
import { useStore } from "vuex";

import SimpleTable from "@/components/SimpleTable.vue";
import TagList from "@/Tags/TagList.vue";
import TagMenu from "@/Tags/TagMenu.vue";
import TagModal from "@/Tags/TagModal.vue";
import { Customer } from "@/typings/customer";
import { Tag } from "@/typings/tag";

const props = defineProps<{
  customer: Customer;
}>();
const store = useStore();
const selectedCustomer = ref<Customer>();
const newTag = ref(Object.assign(new Tag(), { taggable_type: "customer" }));
const showTagMenu = ref(false);
const showTagModal = ref(false);
const tagListConfig = {
  editable: false,
  removeable: true,
  clickEmit: ""
};
const toggleTagMenu = () => {
  showTagMenu.value = !showTagMenu.value;
};

const maxTags = window.currentTenant.tags_config.customer.max_allowed;

const toggleNewTagModal = (label) => {
  newTag.value.label = label;
  showTagModal.value = !showTagModal.value;
};

const handleSaveSuccess = (tag) => {
  toggleNewTagModal(tag.label);
  handleAddTag(tag);
};

const handleAddTag = async (tag) => {
  if (!selectedCustomer.value) throw new Error("Customer is not defined");
  const newTag = await store.dispatch("addTag", {
    tag_id: tag.id,
    customer_id: selectedCustomer.value.id,
    tag_type: "customer"
  });
  selectedCustomer.value.tags?.push(newTag.data);
};

const handleRemoveTag = async (tag) => {
  if (!selectedCustomer.value) throw new Error("Customer is not defined");
  const response = await store.dispatch("removeTag", {
    tag_id: tag.id,
    customer_id: selectedCustomer.value.id,
    tag_type: "customer"
  });
  selectedCustomer.value.tags = selectedCustomer?.value.tags?.filter(
    (t) => t.id !== response.data.id
  );
};

watch(
  () => props.customer,
  (newCustomer) => {
    selectedCustomer.value = newCustomer;
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.label-text {
  white-space: nowrap;
  color: var(--sera-color-core-blue-cobalt-blue-800);
}

.list-item {
  td {
    white-space: nowrap;
  }

  &.list-item-collapsed {
    visibility: collapse;
  }
}

.info {
  color: #5d5d5d;
}

.info-header {
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
}

.info-icon {
  width: 50px;
  text-align: center;
}

.number-badge {
  display: flex;
  height: 18px;
  padding: 0 4px;
  line-height: 18px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 1px solid var(--sera-color-neutral-gray-med-gray-500);
  background: var(--sera-color-neutral-gray-light-gray-100);
  font-size: 12px;
  font-weight: 600;
}

.tag-menu-btn {
  color: var(--sera-color-text-links);
  font-size: 18px;
  margin-left: auto;
  display: flex;
  justify-content: end;
}
</style>
